import {Box} from 'src/design/atoms/Box';
import {Text} from 'src/design/atoms/Text';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useMemo} from 'react';
import {FormContentHeader} from 'src/app/forms/FormHeader';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {identifierFormFieldV2} from 'src/types/FormV2';
import type {IFormFieldV2} from 'src/types/FormV2';
import {useGetFieldList, useGetFormSettings} from '../hooks/useFormBuilder';
import {useFormBuilderRegistry} from '../registry';

export const FormPreview: React.FC = () => {
  const registry = useFormBuilderRegistry();
  const fieldList = useGetFieldList();
  const formSettings = useGetFormSettings();

  const [debouncedFieldList] = useDebouncedValue(fieldList, 100);
  const [debouncedFormSettings] = useDebouncedValue(formSettings, 100);

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    // We insert the Identifier FormField here to represent the static field being
    // present in all forms when it is enabled in the form settings.
    // It cannot be interacted with from the form builder and
    // does not count as a data field.
    let fields = debouncedFieldList;
    if (
      registry.showIdentifierFieldInPreview !== false &&
      debouncedFormSettings.metadata?.enableFormIdentifierField === true
    ) {
      fields = [identifierFormFieldV2, ...fields];
    }

    return fields.map(f => ({
      ...f,
      subFormOptions: {
        ...f.subFormOptions,
        showPreviewComponent: true,
      },
    }));
  }, [debouncedFieldList, debouncedFormSettings, registry]);

  const form = useProactForm({formMeta});

  const PreviewHeader = registry.previewHeader;
  const PreviewLeadIn = registry.previewLeadIn;

  return (
    <Box
      ml={{base: 0, md: 3, lg: 6}}
      mr={{base: 0, md: 3, lg: 6}}
      pos="relative"
      style={{pointerEvents: 'none'}}>
      {PreviewHeader ? (
        <PreviewHeader />
      ) : (
        <FormContentHeader
          text={debouncedFormSettings?.title ?? 'New Form Title'}
          isAnonymousForm={debouncedFormSettings?.metadata.anonymousSubmission}
          internalIdentifier={'[Form ID]'}
          subtext={debouncedFormSettings.metadata?.description}
        />
      )}

      {PreviewLeadIn && <PreviewLeadIn />}

      {formMeta.length > 0 ? (
        <ProactForm form={form}>
          <ProactFormGrid />
        </ProactForm>
      ) : (
        <Text size="sm" c="dimmed">
          No fields to display
        </Text>
      )}
    </Box>
  );
};
